import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import apiClient from '@/lib/api/client';
import { getClients, type Client } from '@/lib/api/clients';
import { getJobs, createJob, type Job } from '@/lib/api/jobs';
import { quotePath } from '@/lib/utils/navPaths';

// ── Job polling ───────────────────────────────────────────────────────────────

interface JobPoll {
  id: string;
  type: 'scan' | 'quote';
  status: 'running' | 'done' | 'error';
  logs: string[];
  result?: unknown;
  error?: string;
}

// Use apiClient (axios) so the 401→refresh interceptor fires automatically.
// Both functions strip the leading /api prefix since apiClient.baseURL is already /api.
async function startJob(url: string, formData: FormData): Promise<string> {
  const path = url.replace(/^\/api/, '');
  const res = await apiClient.post<{ jobId: string }>(path, formData);
  return res.data.jobId;
}

class JobGoneError extends Error {}

async function pollJob(jobId: string): Promise<JobPoll> {
  try {
    const res = await apiClient.get<JobPoll>(`/ai/jobs/${jobId}`);
    return res.data;
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) throw new JobGoneError('This job is no longer available — the server may have restarted. Please run it again.');
    throw err;
  }
}

// Stop retrying after this many consecutive transient (non-404) poll failures.
const MAX_POLL_FAILS = 6;

function notify(title: string, body: string, icon?: string) {
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(title, { body, icon: icon ?? '/favicon.ico' });
  }
}

function requestNotifyPermission() {
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission().catch(() => {/* ignore */});
  }
}

// ── Log box component ─────────────────────────────────────────────────────────

const LogBox = ({ lines, title = 'claude — analysis log' }: { lines: string[]; title?: string }) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [lines.length]);

  return (
    <div className="w-full rounded-xl border border-gray-700 bg-gray-950 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-800 bg-gray-900">
        <span className="w-2.5 h-2.5 rounded-full bg-red-500 opacity-80" />
        <span className="w-2.5 h-2.5 rounded-full bg-yellow-500 opacity-80" />
        <span className="w-2.5 h-2.5 rounded-full bg-green-500 opacity-80" />
        <span className="ml-2 text-xs text-gray-500 font-mono">{title}</span>
      </div>
      <div className="h-52 overflow-y-auto px-3 py-2 font-mono text-xs leading-relaxed">
        {lines.length === 0 ? (
          <span className="text-gray-600">Starting job…</span>
        ) : (
          lines.map((line, i) => (
            <div key={i} className="flex gap-2 text-gray-300">
              <span className="text-gray-600 flex-shrink-0 select-none">›</span>
              <span>{line}</span>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
};

// ── Types ─────────────────────────────────────────────────────────────────────

interface WizardLineItem {
  item_name: string;
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  rate_type?: 'installed' | 'labour' | 'material';
  display_order: number;
}

interface WizardSection {
  name: string;
  items: WizardLineItem[];
}

interface WizardEmail {
  subject: string;
  body: string;
}

interface ProjectInfo {
  estimatedSqFt: number | null;
  stories: number | null;
  foundationType: string | null;
  wallHeight: string | null;
  garageSqFt: number | null;
  roofPitch: string | null;
  roofType: string | null;
  perimeterLF: number | null;
  claddingType: string | null;
  notes: string;
}

interface WizardResult {
  projectInfo: ProjectInfo;
  sections: WizardSection[];
  email: WizardEmail;
  judgements: string[];
}

type Step = 1 | 2 | 3 | 4 | 5;

// ── Phase definitions ─────────────────────────────────────────────────────────

interface PhaseOption {
  id: string;
  label: string;
  icon: string;
}

const PHASE_GROUPS = [
  {
    id: 'structural',
    label: 'Structural',
    color: 'blue',
    phases: [
      { id: 'site_prep',    label: 'Site Prep & Layout',  icon: 'location_on' },
      { id: 'excavation',   label: 'Excavation',          icon: 'construction' },
      { id: 'foundation',   label: 'Foundation',          icon: 'foundation' },
      { id: 'slab',         label: 'Slab on Grade',       icon: 'grid_on' },
      { id: 'floor_system', label: 'Floor System',        icon: 'layers' },
      { id: 'wall_framing', label: 'Wall Framing',        icon: 'straighten' },
      { id: 'roof_framing', label: 'Roof Framing',        icon: 'roofing' },
    ],
  },
  {
    id: 'lockup',
    label: 'Lockup',
    color: 'green',
    phases: [
      { id: 'windows_skylights', label: 'Windows & Skylights', icon: 'window' },
      { id: 'exterior_doors',    label: 'Exterior Doors',      icon: 'door_front' },
      { id: 'roofing',           label: 'Roofing',             icon: 'roofing' },
      { id: 'exterior_envelope', label: 'Exterior Envelope',   icon: 'home' },
    ],
  },
  {
    id: 'interior_rough',
    label: 'Interior Rough',
    color: 'amber',
    phases: [
      { id: 'insulation', label: 'Insulation & Vapour', icon: 'thermostat' },
      { id: 'drywall',    label: 'Drywall',              icon: 'wallpaper' },
    ],
  },
  {
    id: 'finishing',
    label: 'Finishing',
    color: 'purple',
    phases: [
      { id: 'interior_doors', label: 'Interior Doors',  icon: 'meeting_room' },
      { id: 'trim_millwork',  label: 'Trim & Millwork', icon: 'border_style' },
      { id: 'stairs',         label: 'Stairs',          icon: 'stairs' },
      { id: 'cabinetry',      label: 'Cabinetry',       icon: 'kitchen' },
      { id: 'final_hardware', label: 'Final Hardware',  icon: 'hardware' },
    ],
  },
  {
    id: 'outdoors',
    label: 'Outdoor',
    color: 'teal',
    phases: [
      { id: 'decks', label: 'Decks & Outdoor Structures', icon: 'deck' },
    ],
  },
] as const;

const ALL_PHASES = PHASE_GROUPS.flatMap(g => g.phases.map(p => p.id));

type PhaseId = string;

// ── Presets ───────────────────────────────────────────────────────────────────

const PRESETS: { label: string; phases: PhaseId[] }[] = [
  {
    label: 'Full Custom Home',
    phases: ALL_PHASES,
  },
  {
    label: 'Foundation to Framing',
    phases: ['site_prep', 'excavation', 'foundation', 'slab', 'floor_system', 'wall_framing', 'roof_framing'],
  },
  {
    label: 'Framing to Lockup',
    phases: ['floor_system', 'wall_framing', 'roof_framing', 'windows_skylights', 'exterior_doors', 'roofing', 'exterior_envelope'],
  },
  {
    label: 'Lockup Package',
    phases: ['windows_skylights', 'exterior_doors', 'roofing', 'exterior_envelope'],
  },
  {
    label: 'Interior Finishing',
    phases: ['insulation', 'drywall', 'interior_doors', 'trim_millwork', 'stairs', 'cabinetry', 'final_hardware'],
  },
  {
    label: 'Foundation Only',
    phases: ['site_prep', 'excavation', 'foundation', 'slab'],
  },
  {
    label: 'Framing Only',
    phases: ['floor_system', 'wall_framing', 'roof_framing'],
  },
];

// ── Project parameter options ─────────────────────────────────────────────────

const PARAM_OPTIONS: {
  key: string;
  label: string;
  options: string[];
}[] = [
  { key: 'construction_type', label: 'Construction Type',  options: ['New Build', 'Addition', 'Renovation'] },
  { key: 'foundation_type',   label: 'Foundation Type',    options: ['Crawl Space', 'Full Basement', 'Slab on Grade', 'Perimeter Stem Wall'] },
  { key: 'stories',           label: 'Stories',            options: ['1', '1.5', '2', '2.5', '3'] },
  { key: 'garage',            label: 'Garage',             options: ['None', 'Single Attached', 'Double Attached', 'Triple Attached', 'Detached'] },
  { key: 'roof_type',         label: 'Roof Covering',      options: ['Metal Standing Seam', 'Asphalt Shingles', 'EPDM Flat', 'Mixed Metal + EPDM'] },
  { key: 'cladding_type',     label: 'Cladding',           options: ['LP SmartSide', 'HardiePlank', 'Cedar Bevel', 'Vinyl Siding', 'Stucco', 'Mixed'] },
  { key: 'window_grade',      label: 'Window Grade',       options: ['Builder (JELD-WEN)', 'Mid (Kohltech / Ply Gem)', 'Premium (Cascadia / Loewen)'] },
  { key: 'flooring_type',     label: 'Flooring',           options: ['Hardwood', 'Engineered Hardwood', 'Laminate', 'Tile', 'Carpet', 'Mixed'] },
];

const GROUP_COLORS: Record<string, string> = {
  structural:      'border-blue-300   bg-blue-50   text-blue-700',
  lockup:          'border-green-300  bg-green-50  text-green-700',
  interior_rough:  'border-amber-300  bg-amber-50  text-amber-700',
  finishing:       'border-purple-300 bg-purple-50 text-purple-700',
  outdoors:        'border-teal-300   bg-teal-50   text-teal-700',
};

const GROUP_HEADER_COLORS: Record<string, string> = {
  structural:      'text-blue-700   border-blue-200   bg-blue-50/60',
  lockup:          'text-green-700  border-green-200  bg-green-50/60',
  interior_rough:  'text-amber-700  border-amber-200  bg-amber-50/60',
  finishing:       'text-purple-700 border-purple-200 bg-purple-50/60',
  outdoors:        'text-teal-700   border-teal-200   bg-teal-50/60',
};

const fmt = (n: number) =>
  `$${n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ── Component ─────────────────────────────────────────────────────────────────

export const QuoteWizardPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Step 1 state
  const [step, setStep] = useState<Step>(1);
  const [selectedPhases, setSelectedPhases] = useState<Set<PhaseId>>(
    new Set(['wall_framing', 'roof_framing', 'floor_system'])
  );
  const [projectParams, setProjectParams] = useState<Record<string, string>>({});
  const [title, setTitle] = useState('');
  const [siteAddress, setSiteAddress] = useState('');
  const [clientId, setClientId] = useState('');
  const [jobId, setJobId] = useState('');
  const [description, setDescription] = useState('');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfObjectUrl, setPdfObjectUrl] = useState<string | null>(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  // Markup
  const [markupPct, setMarkupPct] = useState(0);
  const [markupCustom, setMarkupCustom] = useState('');

  // Scan job
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const [scanInfo, setScanInfo] = useState<ProjectInfo | null>(null);
  const [scanPagesAnalyzed, setScanPagesAnalyzed] = useState(0);
  const [scanLogs, setScanLogs] = useState<string[]>([]);
  const [scanJobId, setScanJobId] = useState<string | null>(null);

  // Estimate job
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState('');
  const [estimateLogs, setEstimateLogs] = useState<string[]>([]);
  const [estimateJobId, setEstimateJobId] = useState<string | null>(null);

  // Notification banner
  const [notification, setNotification] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  // Elapsed seconds while a scan/estimate job runs (for live feedback + safety net)
  const [elapsed, setElapsed] = useState(0);

  // Hard frontend wall-clock: if a job hasn't finished in this long, stop waiting
  // so the UI can never sit on the spinner forever.
  const ANALYSIS_MAX_SECONDS = 6 * 60; // 6 min (> backend 5-min cap)

  // Step 3 state
  const [projectInfo, setProjectInfo] = useState<ProjectInfo | null>(null);
  const [sections, setSections] = useState<WizardSection[]>([]);
  const [email, setEmail] = useState<WizardEmail>({ subject: '', body: '' });
  const [judgements, setJudgements] = useState<string[]>([]);
  const [pagesAnalyzed, setPagesAnalyzed] = useState(0);
  const [showEmail, setShowEmail] = useState(true);
  const [showJudgements, setShowJudgements] = useState(false);
  const [saving, setSaving] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  // Step 4 state
  const [createdQuoteId, setCreatedQuoteId] = useState<number | null>(null);
  const [createdQuoteNumber, setCreatedQuoteNumber] = useState('');

  useEffect(() => {
    getClients().then(setClients).catch(() => {});
    getJobs({ limit: 500 }).then(r => setJobs(r.jobs)).catch(() => {});
    requestNotifyPermission();

    // Pre-fill from URL params (?jobId=X&clientId=Y&title=Z&address=Z)
    const paramJobId    = searchParams.get('jobId');
    const paramClientId = searchParams.get('clientId');
    const paramTitle    = searchParams.get('title');
    const paramAddress  = searchParams.get('address');
    if (paramJobId)    setJobId(paramJobId);
    if (paramClientId) setClientId(paramClientId);
    if (paramTitle)    setTitle(paramTitle);
    if (paramAddress)  setSiteAddress(paramAddress);

    // Resume any in-progress jobs from a previous session
    const savedScanId = localStorage.getItem('wiz_scan_job');
    const savedEstId  = localStorage.getItem('wiz_est_job');
    if (savedScanId) { setScanJobId(savedScanId); setScanning(true); }
    if (savedEstId)  { setEstimateJobId(savedEstId); setAnalyzing(true); setStep(3); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Manage PDF object URL lifecycle
  useEffect(() => {
    if (!pdfFile) { setPdfObjectUrl(null); return; }
    const url = URL.createObjectURL(pdfFile);
    setPdfObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pdfFile]);

  // Poll scan job
  useEffect(() => {
    if (!scanJobId) return;
    let active = true;
    let fails = 0;
    const fail = (msg: string) => {
      localStorage.removeItem('wiz_scan_job');
      setScanJobId(null);
      setScanning(false);
      setScanError(msg);
      notify('Scan failed', msg);
      setNotification({ kind: 'error', msg: `Scan failed: ${msg}` });
    };
    const tick = async () => {
      try {
        const job = await pollJob(scanJobId);
        if (!active) return;
        fails = 0;
        setScanLogs(job.logs);
        if (job.status === 'done') {
          localStorage.removeItem('wiz_scan_job');
          setScanJobId(null);
          setScanning(false);
          const res = job.result as { data: { detectedPhases: string[]; projectInfo: ProjectInfo }; pagesAnalyzed: number };
          const { detectedPhases, projectInfo: pi } = res.data;
          const newPhases = new Set<PhaseId>(detectedPhases.filter((p): p is PhaseId => (ALL_PHASES as readonly string[]).includes(p)));
          if (newPhases.size > 0) setSelectedPhases(newPhases);
          if (pi.estimatedSqFt && !projectParams.sqft) setProjectParams(prev => ({ ...prev, sqft: `${pi.estimatedSqFt} SF` }));
          if (pi.stories && !projectParams.stories) setProjectParams(prev => ({ ...prev, stories: `${pi.stories}-storey` }));
          setScanInfo(pi);
          setScanPagesAnalyzed(res.pagesAnalyzed ?? 0);
          setStep(2);
          notify('Plans scanned', `Detected ${newPhases.size} phase${newPhases.size !== 1 ? 's' : ''}`);
          setNotification({ kind: 'success', msg: `Plan scan complete — ${newPhases.size} phase${newPhases.size !== 1 ? 's' : ''} detected` });
        } else if (job.status === 'error') {
          fail(job.error ?? 'Unknown error');
        } else {
          setTimeout(tick, 2000);
        }
      } catch (err) {
        if (!active) return;
        if (err instanceof JobGoneError) { fail(err.message); return; }
        if (++fails >= MAX_POLL_FAILS) { fail('Lost connection to the analysis job. Please try again.'); return; }
        setTimeout(tick, 3000);
      }
    };
    tick();
    return () => { active = false; };
  }, [scanJobId]);

  // Poll estimate job
  useEffect(() => {
    if (!estimateJobId) return;
    let active = true;
    let fails = 0;
    const fail = (msg: string) => {
      localStorage.removeItem('wiz_est_job');
      setEstimateJobId(null);
      setAnalyzing(false);
      setAnalyzeError(msg);
      notify('Estimate failed', msg);
      setNotification({ kind: 'error', msg: `Estimate failed: ${msg}` });
    };
    const tick = async () => {
      try {
        const job = await pollJob(estimateJobId);
        if (!active) return;
        fails = 0;
        setEstimateLogs(job.logs);
        if (job.status === 'done') {
          localStorage.removeItem('wiz_est_job');
          setEstimateJobId(null);
          setAnalyzing(false);
          const res = job.result as { data: WizardResult; pagesAnalyzed: number };
          const data = res.data;
          setProjectInfo(data.projectInfo);
          setSections(data.sections.map(s => ({ ...s, items: s.items.map((it, idx) => ({ ...it, display_order: idx + 1 })) })));
          setEmail(data.email ?? { subject: '', body: '' });
          setJudgements(data.judgements ?? []);
          setPagesAnalyzed(res.pagesAnalyzed ?? 0);
          setStep(4);
          notify('Estimate ready', 'Your quote wizard estimate is complete');
          setNotification({ kind: 'success', msg: 'Estimate complete — review your line items below' });
        } else if (job.status === 'error') {
          fail(job.error ?? 'Unknown error');
        } else {
          setTimeout(tick, 2000);
        }
      } catch (err) {
        if (!active) return;
        if (err instanceof JobGoneError) { fail(err.message); return; }
        if (++fails >= MAX_POLL_FAILS) { fail('Lost connection to the estimate job. Please try again.'); return; }
        setTimeout(tick, 3000);
      }
    };
    tick();
    return () => { active = false; };
  }, [estimateJobId]);

  // Elapsed-time ticker + wall-clock safety net so a running job can never wedge the UI.
  useEffect(() => {
    if (!scanning && !analyzing) { setElapsed(0); return; }
    const start = Date.now();
    const iv = setInterval(() => {
      const secs = Math.floor((Date.now() - start) / 1000);
      setElapsed(secs);
      if (secs < ANALYSIS_MAX_SECONDS) return;
      // Backend should have failed by now; stop waiting regardless.
      if (analyzing) {
        localStorage.removeItem('wiz_est_job');
        setEstimateJobId(null);
        setAnalyzing(false);
        setAnalyzeError('This took longer than expected and was stopped. Please try again — ideally with fewer phases or pages.');
        setNotification({ kind: 'error', msg: 'Estimate timed out — please try again.' });
      }
      if (scanning) {
        localStorage.removeItem('wiz_scan_job');
        setScanJobId(null);
        setScanning(false);
        setScanError('Plan scan timed out — please try again.');
        setNotification({ kind: 'error', msg: 'Plan scan timed out — please try again.' });
      }
    }, 1000);
    return () => clearInterval(iv);
  }, [scanning, analyzing]);

  const clientJobs = clientId
    ? jobs.filter(j => String(j.clientId) === clientId || String((j as any).client_id) === clientId)
    : jobs;

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f?.type === 'application/pdf' || f?.name.endsWith('.pdf')) { setPdfFile(f); setScanInfo(null); }
  }, []);

  // ── Step 1: Scan PDF → submit background job ──────────────────────────────

  const scanPlans = async () => {
    setScanning(true);
    setScanError('');
    setScanLogs([]);
    try {
      const fd = new FormData();
      if (pdfFile) fd.append('file', pdfFile);
      if (description) fd.append('description', description);
      const jobId = await startJob('/api/ai/analyse-plans', fd);
      localStorage.setItem('wiz_scan_job', jobId);
      setScanJobId(jobId);
      requestNotifyPermission();
    } catch (err: unknown) {
      setScanning(false);
      setScanError(err instanceof Error ? err.message : 'Could not start scan');
    }
  };

  const togglePhase = (id: PhaseId) => {
    setSelectedPhases(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const applyPreset = (phases: PhaseId[]) => {
    setSelectedPhases(new Set(phases));
  };

  const setParam = (key: string, value: string) => {
    setProjectParams(prev => ({ ...prev, [key]: prev[key] === value ? '' : value }));
  };

  const sendEmail = async () => {
    setSendingEmail(true);
    try {
      const client = clients.find(c => c.id === clientId);
      const to = client?.email;
      if (!to) { setNotification({ kind: 'error', msg: 'No email address on file for this client. Add one on the client record, or select a client first.' }); return; }
      await apiClient.post('/ai/send-wizard-email', {
        to,
        subject: email.subject,
        body: email.body,
        quote_id: createdQuoteId,
      });
      setEmailSent(true);
      setNotification({ kind: 'success', msg: 'Email sent to the client.' });
    } catch (err: any) {
      setNotification({ kind: 'error', msg: err?.response?.data?.error || 'Failed to send email.' });
    } finally {
      setSendingEmail(false);
    }
  };

  // ── Step 1 → 2: Run analysis ──────────────────────────────────────────────

  const runAnalysis = async () => {
    // Compute effective values (state setters are async — derive locally)
    const clientName = clients.find(c => c.id === clientId)?.name ?? '';
    const effectiveTitle = title.trim()
      || (clientName ? `${clientName} — Estimate` : `Quote ${new Date().toLocaleDateString('en-CA')}`);
    const effectivePhases = selectedPhases.size > 0
      ? selectedPhases
      : new Set(['floor_system', 'wall_framing', 'roof_framing']);

    // Sync back to state so UI reflects what was used
    setTitle(effectiveTitle);
    setSelectedPhases(effectivePhases);

    setStep(3);
    setAnalyzing(true);
    setAnalyzeError('');
    setEstimateLogs([]);

    try {
      const orderedPhases = ALL_PHASES.filter(p => effectivePhases.has(p));
      const fd = new FormData();
      fd.append('phases', JSON.stringify(orderedPhases));
      fd.append('projectParams', JSON.stringify(projectParams));
      fd.append('title', effectiveTitle);
      fd.append('site_address', siteAddress);
      fd.append('client_name', clients.find(c => c.id === clientId)?.name || '');
      fd.append('description', description);
      if (projectInfo) fd.append('scanProjectInfo', JSON.stringify(projectInfo));
      if (pdfFile) fd.append('file', pdfFile);

      const jobId = await startJob('/api/ai/quote-wizard', fd);
      localStorage.setItem('wiz_est_job', jobId);
      setEstimateJobId(jobId);
      requestNotifyPermission();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Could not start estimate';
      setAnalyzeError(msg);
      setAnalyzing(false);
    }
  };

  // Stop watching the running estimate and return to scope. (The background job is
  // abandoned; it finishes or times out server-side and is purged.)
  const cancelAnalysis = () => {
    localStorage.removeItem('wiz_est_job');
    setEstimateJobId(null);
    setAnalyzing(false);
    setEstimateLogs([]);
    setStep(2);
    setNotification({ kind: 'error', msg: 'Estimate cancelled — your scope selections are intact.' });
  };

  const fmtElapsed = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // ── Step 3 editing helpers ─────────────────────────────────────────────────

  const updateItem = (si: number, ii: number, field: keyof WizardLineItem, value: string | number) => {
    setSections(prev => prev.map((s, si2) =>
      si2 !== si ? s : {
        ...s,
        items: s.items.map((it, ii2) => ii2 !== ii ? it : { ...it, [field]: value }),
      }
    ));
  };

  const removeItem = (si: number, ii: number) => {
    setSections(prev => prev.map((s, si2) =>
      si2 !== si ? s : { ...s, items: s.items.filter((_, ii2) => ii2 !== ii) }
    ).filter(s => s.items.length > 0));
  };

  const addItem = (si: number) => {
    setSections(prev => prev.map((s, si2) =>
      si2 !== si ? s : {
        ...s,
        items: [...s.items, { item_name: 'New item', description: '', quantity: 1, unit: 'EA', unit_price: 0, rate_type: 'installed' as const, display_order: s.items.length + 1 }],
      }
    ));
  };

  const renameSection = (si: number, name: string) => {
    setSections(prev => prev.map((s, si2) => si2 !== si ? s : { ...s, name }));
  };

  const sectionTotal = (s: WizardSection) =>
    s.items.reduce((acc, it) => acc + it.quantity * it.unit_price, 0);

  const clampPct = (n: number) => Math.max(0, Math.min(100, n));
  const grandTotal = sections.reduce((acc, s) => acc + sectionTotal(s), 0);
  const labourTotal = sections.reduce((acc, s) =>
    acc + s.items.filter(it => it.rate_type === 'labour').reduce((a, it) => a + it.quantity * it.unit_price, 0), 0);
  const fixedTotal = grandTotal - labourTotal;
  const effectiveMarkupPct = clampPct(markupPct > 0 ? markupPct : parseFloat(markupCustom) || 0);
  const markupAmount = grandTotal * (effectiveMarkupPct / 100);
  const grandTotalWithMarkup = grandTotal + markupAmount;

  // ── Step 3 → 4: Create quote ───────────────────────────────────────────────

  const createQuote = async () => {
    setSaving(true);
    try {
      // A quote persists its title + client only through a linked job. If the user
      // didn't link one, auto-create a lightweight "draft" job from the wizard's
      // title + client so the quote is never orphaned and the title survives.
      let effectiveJobId = jobId;
      if (!effectiveJobId) {
        const jobTitle = title.trim()
          || (clients.find(c => c.id === clientId)?.name
            ? `${clients.find(c => c.id === clientId)!.name} — Estimate`
            : `Estimate ${new Date().toLocaleDateString('en-CA')}`);
        const newJob = await createJob({
          title: jobTitle,
          client_id: clientId ? Number(clientId) : null,
          site_address: siteAddress || null,
          description: description || null,
          start_date: null,
          estimated_end_date: null,
          status: 'draft',
        });
        effectiveJobId = String(newJob.id);
        setJobId(effectiveJobId);
      }

      const qRes = await apiClient.post('/quotes', { jobId: effectiveJobId });
      const { id, quoteNumber } = qRes.data.data;

      const baseSections = sections.map((s, si) => ({
        name: s.name,
        display_order: si + 1,
        items: s.items.map((it, ii) => ({
          item_name: it.item_name,
          description: it.rate_type === 'labour'
            ? `[T&M·HR] ${it.description}`.trim()
            : it.rate_type === 'material'
              ? `[SUPPLY] ${it.description}`.trim()
              : it.description,
          quantity: Number(it.quantity),
          unit_price: Number(it.unit_price),
          unit: it.unit,
          display_order: ii + 1,
        })),
      }));

      const effectiveMarkup = clampPct(markupPct > 0 ? markupPct : parseFloat(markupCustom) || 0);
      const markupAmount = grandTotal * (effectiveMarkup / 100);
      const payload = effectiveMarkup > 0
        ? [
            ...baseSections,
            {
              name: `Contingency & Markup (${effectiveMarkup}%)`,
              display_order: baseSections.length + 1,
              items: [{
                item_name: `${effectiveMarkup}% Markup`,
                description: `Applied on $${grandTotal.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} subtotal`,
                quantity: 1,
                unit_price: markupAmount,
                unit: 'LS',
                display_order: 1,
              }],
            },
          ]
        : baseSections;

      await apiClient.put(`/quotes/${id}/sections`, { sections: payload });

      setCreatedQuoteId(id);
      setCreatedQuoteNumber(quoteNumber);
      setStep(5);
    } catch (err: any) {
      setNotification({ kind: 'error', msg: err?.response?.data?.message || 'Failed to save quote.' });
    } finally {
      setSaving(false);
    }
  };

  const resetWizard = () => {
    setStep(1); setPdfFile(null); setTitle(''); setDescription('');
    setSiteAddress(''); setClientId(''); setJobId(''); setSections([]);
    setProjectInfo(null); setEmail({ subject: '', body: '' });
    setJudgements([]); setEmailSent(false);
    setScanInfo(null); setScanError('');
    setSelectedPhases(new Set(['wall_framing', 'roof_framing', 'floor_system']));
    setMarkupPct(0); setMarkupCustom(''); setProjectParams({});
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <MainLayout>
      {/* PDF preview overlay */}
      {showPdfPreview && pdfObjectUrl && (
        <div className="fixed inset-0 z-50 flex flex-col bg-gray-950/95">
          <div className="flex items-center justify-between px-4 py-3 bg-gray-900 border-b border-gray-700 flex-shrink-0">
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-white text-xl">picture_as_pdf</span>
              <span className="text-sm font-semibold text-white truncate max-w-xs">{pdfFile?.name}</span>
            </div>
            <button
              onClick={() => setShowPdfPreview(false)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-700 text-gray-200 text-sm hover:bg-gray-600 transition-colors"
            >
              <span className="material-symbols-outlined text-base">close</span>
              Close
            </button>
          </div>
          <iframe
            src={pdfObjectUrl}
            className="flex-1 w-full"
            title="Builder's Plans"
          />
        </div>
      )}

      {/* Header */}
      <div className="sticky top-14 md:top-0 z-20 border-b border-gray-200 px-4 py-4 md:px-8 md:py-5 bg-gray-50/80 backdrop-blur-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/quotes')} className="p-1.5 rounded-lg hover:bg-gray-200 text-gray-500">
              <span className="material-symbols-outlined text-xl">arrow_back</span>
            </button>
            <div className="flex items-center gap-3">
              <div className="bg-primary/10 rounded-md p-2">
                <span className="material-symbols-outlined text-primary text-xl">auto_awesome</span>
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">AI Quote Wizard</h2>
                <p className="text-sm text-gray-500">Full-scope carpentry estimating from builder's plans</p>
              </div>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-1.5">
            {(['Upload', 'Scope', 'Generating', 'Review', 'Done'] as const).map((label, i) => (
              <div key={label} className="flex items-center gap-1.5">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                  step === i + 1 ? 'bg-primary text-white' :
                  step > i + 1 ? 'bg-green-500 text-white' :
                  'bg-gray-200 text-gray-500'
                }`}>
                  {step > i + 1 ? <span className="material-symbols-outlined text-sm">check</span> : i + 1}
                </div>
                <span className={`text-xs font-medium ${step === i + 1 ? 'text-primary' : 'text-gray-400'}`}>{label}</span>
                {i < 4 && <span className="text-gray-300 text-xs">›</span>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Notification banner ───────────────────────────────────────────── */}
      {notification && (
        <div className={`flex items-center gap-3 px-4 py-3 text-sm font-medium ${
          notification.kind === 'success'
            ? 'bg-green-50 border-b border-green-200 text-green-800'
            : 'bg-red-50 border-b border-red-200 text-red-800'
        }`}>
          <span className="material-symbols-outlined text-base flex-shrink-0">
            {notification.kind === 'success' ? 'check_circle' : 'error'}
          </span>
          <span className="flex-1">{notification.msg}</span>
          <button onClick={() => setNotification(null)} className="flex-shrink-0 opacity-60 hover:opacity-100">
            <span className="material-symbols-outlined text-base">close</span>
          </button>
        </div>
      )}

      <div className="p-4 md:p-8 max-w-5xl mx-auto">

        {/* ── STEP 1: Upload ────────────────────────────────────────────────── */}
        {step === 1 && (
          <div className="space-y-6">

            {/* PDF Upload — first because scope is calibrated to what's in the plans */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <h3 className="font-bold text-gray-900 mb-1">Builder's Plans</h3>
              <p className="text-sm text-gray-500 mb-4">
                Upload the PDF first — scope selections on the next step are calibrated to what's in the drawings.
                No PDF? You can describe the project in the notes and proceed without one.
              </p>

              <div
                onDrop={onDrop}
                onDragOver={e => e.preventDefault()}
                onClick={() => fileRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
                  pdfFile ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-primary hover:bg-primary/5'
                }`}
              >
                {pdfFile ? (
                  <div className="flex flex-col items-center gap-2">
                    <span className="material-symbols-outlined text-green-500 text-5xl">picture_as_pdf</span>
                    <p className="font-semibold text-green-700 text-base">{pdfFile.name}</p>
                    <p className="text-xs text-green-600">{(pdfFile.size / 1024 / 1024).toFixed(1)} MB — up to 10 pages will be analysed</p>
                    <button
                      onClick={e => { e.stopPropagation(); setPdfFile(null); setScanInfo(null); }}
                      className="text-xs text-red-500 hover:underline mt-1"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <span className="material-symbols-outlined text-gray-400 text-5xl">upload_file</span>
                    <p className="font-medium text-gray-700">Drop PDF here or click to browse</p>
                    <p className="text-xs text-gray-400">Max 50 MB · PDF only · First 10 pages analysed</p>
                  </div>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  className="hidden"
                  onChange={e => { if (e.target.files?.[0]) { setPdfFile(e.target.files[0]); setScanInfo(null); } }}
                />
              </div>
            </div>

            {/* Project details */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <h3 className="font-bold text-gray-900 mb-4">Project Details</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project Title</label>
                  <input
                    type="text"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    placeholder="e.g. Johnson Residence — Wood Frame"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                    <select
                      value={clientId}
                      onChange={e => { setClientId(e.target.value); setJobId(''); }}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none bg-white"
                    >
                      <option value="">— Select client —</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ''}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Link to Job</label>
                    <select
                      value={jobId}
                      onChange={e => setJobId(e.target.value)}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none bg-white"
                    >
                      <option value="">— No job —</option>
                      {clientJobs.map(j => <option key={j.id} value={j.id}>{(j as any).job_number || j.id} — {j.title}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Site Address</label>
                  <input
                    type="text"
                    value={siteAddress}
                    onChange={e => setSiteAddress(e.target.value)}
                    placeholder="123 Main St, Duncan, BC"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Heated Floor Area <span className="font-normal text-gray-400">(SF)</span>
                    </label>
                    <input
                      type="number"
                      min={0}
                      value={projectParams.sqft?.replace(/\D/g, '') ?? ''}
                      onChange={e => setProjectParams(prev => ({ ...prev, sqft: e.target.value ? `${e.target.value} SF` : '' }))}
                      placeholder="e.g. 2400"
                      className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Garage Area <span className="font-normal text-gray-400">(SF, optional)</span>
                    </label>
                    <input
                      type="number"
                      min={0}
                      value={projectParams.garage_sqft?.replace(/\D/g, '') ?? ''}
                      onChange={e => setProjectParams(prev => ({ ...prev, garage_sqft: e.target.value ? `${e.target.value} SF garage` : '' }))}
                      placeholder="e.g. 576"
                      className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes / Key Dimensions
                    <span className="font-normal text-gray-400 ml-1">(optional — supplements the PDF)</span>
                  </label>
                  <textarea
                    rows={3}
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder="e.g. 2,400 SF main floor, 9' ceilings, double attached garage 24×24, 6/12 pitch, clerestory window wall in great room…"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none resize-y"
                  />
                </div>
              </div>
            </div>

            {scanError && (
              <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
                <span className="material-symbols-outlined text-red-400 flex-shrink-0">error</span>
                <span className="flex-1">{scanError}</span>
                <button onClick={() => setStep(2)} className="text-xs font-semibold underline flex-shrink-0">Skip to scope</button>
              </div>
            )}

            <div className="flex items-center justify-between">
              <button
                onClick={() => setStep(2)}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Skip — select scope manually
              </button>
              <button
                onClick={pdfFile ? scanPlans : () => setStep(2)}
                disabled={scanning}
                className="flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-xl font-semibold text-sm hover:bg-primary/90 disabled:opacity-70 transition-colors"
              >
                {scanning ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Analysing plans…
                  </>
                ) : pdfFile ? (
                  <>
                    <span className="material-symbols-outlined text-base">document_scanner</span>
                    Analyse Plans
                  </>
                ) : (
                  <>
                    Next: Select Scope
                    <span className="material-symbols-outlined text-base">arrow_forward</span>
                  </>
                )}
              </button>
            </div>

            {/* Scan log box — shown while scanning */}
            {scanning && (
              <div className="mt-4">
                <LogBox lines={scanLogs} />
              </div>
            )}

          </div>
        )}

        {/* ── STEP 2: Scope ─────────────────────────────────────────────────── */}
        {step === 2 && (
          <div className="space-y-6">

            {/* Scan result banner */}
            {scanInfo ? (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-blue-500 flex-shrink-0 mt-0.5">document_scanner</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-blue-800 text-sm">Plans Analysed</p>
                      <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">{scanPagesAnalyzed} page{scanPagesAnalyzed !== 1 ? 's' : ''}</span>
                    </div>
                    <p className="text-sm text-blue-700">{scanInfo.notes}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-blue-600">
                      {scanInfo.estimatedSqFt ? <span>~{scanInfo.estimatedSqFt.toLocaleString()} SF</span> : null}
                      {scanInfo.stories ? <span>{scanInfo.stories}-storey</span> : null}
                      {scanInfo.foundationType && scanInfo.foundationType !== 'unknown' ? <span className="capitalize">{scanInfo.foundationType}</span> : null}
                      {scanInfo.wallHeight ? <span>{scanInfo.wallHeight} ceilings</span> : null}
                      {scanInfo.garageSqFt ? <span>+{scanInfo.garageSqFt} SF garage</span> : null}
                      {scanInfo.roofPitch ? <span>{scanInfo.roofPitch} pitch</span> : null}
                    </div>
                    <p className="text-xs text-blue-500 mt-2">Phases pre-selected below from what was detected — adjust as needed.</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    {pdfObjectUrl && (
                      <button onClick={() => setShowPdfPreview(true)} className="text-xs text-blue-600 hover:underline flex-shrink-0">View PDF</button>
                    )}
                    <button onClick={() => setStep(1)} className="text-xs text-blue-600 hover:underline flex-shrink-0">Change</button>
                  </div>
                </div>
              </div>
            ) : pdfFile ? (
              <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                <span className="material-symbols-outlined text-green-500">picture_as_pdf</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-green-800 truncate">{pdfFile.name}</p>
                  <p className="text-xs text-green-600">{(pdfFile.size / 1024 / 1024).toFixed(1)} MB · ready for analysis</p>
                </div>
                <button onClick={() => setShowPdfPreview(true)} className="text-xs text-green-700 hover:underline flex-shrink-0">View</button>
                <button onClick={() => setStep(1)} className="text-xs text-green-700 hover:underline flex-shrink-0">Change</button>
              </div>
            ) : (
              <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                <span className="material-symbols-outlined text-amber-500">info</span>
                <p className="text-sm text-amber-800 flex-1">No PDF — estimate will use project parameters and notes only.
                  <button onClick={() => setStep(1)} className="ml-2 font-semibold underline">Upload plans</button>
                </p>
              </div>
            )}

            {/* Presets */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <h3 className="font-bold text-gray-900 mb-1">Scope Presets</h3>
              <p className="text-sm text-gray-500 mb-3">Quick-select a common scope, then fine-tune phases below.</p>
              <div className="flex flex-wrap gap-2">
                {PRESETS.map(p => (
                  <button
                    key={p.label}
                    onClick={() => applyPreset(p.phases)}
                    className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                      p.phases.length === selectedPhases.size && p.phases.every(ph => selectedPhases.has(ph))
                        ? 'bg-primary border-primary text-white'
                        : 'border-gray-200 text-gray-700 hover:border-primary hover:text-primary'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Phase selection grid */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-bold text-gray-900">Phases to Estimate</h3>
                  <p className="text-sm text-gray-500">{selectedPhases.size} of {ALL_PHASES.length} phases selected</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setSelectedPhases(new Set(ALL_PHASES))} className="text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50">All</button>
                  <button onClick={() => setSelectedPhases(new Set())} className="text-xs px-2.5 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50">None</button>
                </div>
              </div>

              <div className="space-y-4">
                {PHASE_GROUPS.map(group => (
                  <div key={group.id}>
                    <div className={`flex items-center justify-between px-3 py-1.5 rounded-lg mb-2 border ${GROUP_HEADER_COLORS[group.id]}`}>
                      <span className="text-xs font-bold uppercase tracking-wider">{group.label}</span>
                      <button
                        onClick={() => {
                          const ids = group.phases.map(p => p.id);
                          const allSelected = ids.every(id => selectedPhases.has(id));
                          setSelectedPhases(prev => {
                            const next = new Set(prev);
                            ids.forEach(id => allSelected ? next.delete(id) : next.add(id));
                            return next;
                          });
                        }}
                        className="text-xs opacity-70 hover:opacity-100 font-medium"
                      >
                        {group.phases.every(p => selectedPhases.has(p.id)) ? 'Deselect all' : 'Select all'}
                      </button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                      {group.phases.map((phase: PhaseOption) => {
                        const active = selectedPhases.has(phase.id);
                        return (
                          <button
                            key={phase.id}
                            onClick={() => togglePhase(phase.id)}
                            className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 text-left transition-all text-sm font-medium ${
                              active
                                ? `${GROUP_COLORS[group.id]} border-current`
                                : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700'
                            }`}
                          >
                            <span className={`material-symbols-outlined text-base flex-shrink-0 ${active ? '' : 'opacity-40'}`}>{phase.icon}</span>
                            <span className="leading-tight">{phase.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Project parameters */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <h3 className="font-bold text-gray-900 mb-1">Project Parameters</h3>
              <p className="text-sm text-gray-500 mb-4">Help the AI apply correct pricing tiers and assumptions.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                {PARAM_OPTIONS.map(param => (
                  <div key={param.key}>
                    <p className="text-sm font-semibold text-gray-700 mb-2">{param.label}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {param.options.map(opt => (
                        <button
                          key={opt}
                          onClick={() => setParam(param.key, opt)}
                          className={`px-2.5 py-1 rounded-md border text-xs font-medium transition-colors ${
                            projectParams[param.key] === opt
                              ? 'bg-primary border-primary text-white'
                              : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-800'
                          }`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer actions */}
            <div className="flex items-center justify-between bg-primary/5 border border-primary/20 rounded-xl p-5">
              <div>
                <p className="font-semibold text-gray-900">
                  {selectedPhases.size > 0
                    ? `${selectedPhases.size} phase${selectedPhases.size !== 1 ? 's' : ''} selected`
                    : 'No phases — will default to Framing'}
                </p>
                <p className="text-xs text-gray-500 mt-0.5 max-w-sm truncate">
                  {selectedPhases.size > 0
                    ? ALL_PHASES.filter(p => selectedPhases.has(p)).map(p => p.replace(/_/g, ' ')).join(' · ')
                    : ''}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  onClick={runAnalysis}
                  className="flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-xl font-semibold text-sm hover:bg-primary/90 transition-colors whitespace-nowrap"
                >
                  <span className="material-symbols-outlined text-base">auto_awesome</span>
                  Generate Quote
                </button>
              </div>
            </div>

          </div>
        )}

        {/* ── STEP 3: Analysing ──────────────────────────────────────────────── */}
        {step === 3 && (
          <div className="flex flex-col gap-6 py-6">
            {analyzing ? (
              <>
                {/* Header row */}
                <div className="flex items-center gap-4">
                  <div className="relative flex-shrink-0">
                    <div className="w-14 h-14 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
                    <span className="material-symbols-outlined text-primary text-xl absolute inset-0 flex items-center justify-center">auto_awesome</span>
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-800 text-lg">Generating Estimate…</p>
                    <p className="text-sm text-gray-500 mt-0.5">
                      {selectedPhases.size} phase{selectedPhases.size !== 1 ? 's' : ''}{pdfFile ? ` · ${pdfFile.name}` : ''}
                      <span className="ml-2 font-mono text-gray-400">{fmtElapsed(elapsed)}</span>
                    </p>
                  </div>
                  <button
                    onClick={cancelAnalysis}
                    className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300"
                  >
                    <span className="material-symbols-outlined text-base">close</span>
                    Cancel
                  </button>
                </div>
                {elapsed >= 45 && (
                  <p className="text-xs text-gray-400 -mt-2">
                    Detailed takeoffs can take a minute or two. You can keep waiting, or cancel and try fewer phases.
                  </p>
                )}
                {/* Live log box */}
                <LogBox lines={estimateLogs} />
              </>
            ) : analyzeError ? (
              <div className="flex flex-col gap-4 max-w-md">
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-red-400 text-4xl">error</span>
                  <p className="font-semibold text-gray-800">Analysis Failed</p>
                </div>
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{analyzeError}</p>
                {estimateLogs.length > 0 && <LogBox lines={estimateLogs} />}
                <div className="flex gap-3">
                  <button onClick={() => setStep(2)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium hover:bg-gray-50">
                    Adjust Scope
                  </button>
                  <button onClick={runAnalysis} className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90">
                    Retry
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        )}

        {/* ── STEP 4: Review ────────────────────────────────────────────────── */}
        {step === 4 && projectInfo && (
          <div className="space-y-6">
            {/* AI summary banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3">
              <span className="material-symbols-outlined text-blue-500 flex-shrink-0 mt-0.5">info</span>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-blue-800 text-sm mb-1">AI Analysis Summary</p>
                <p className="text-sm text-blue-700">{projectInfo.notes}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-blue-600">
                  {projectInfo.estimatedSqFt ? <span>~{projectInfo.estimatedSqFt.toLocaleString()} SF</span> : null}
                  {projectInfo.stories ? <span>{projectInfo.stories}-storey</span> : null}
                  {projectInfo.foundationType ? <span className="capitalize">{projectInfo.foundationType}</span> : null}
                  {projectInfo.wallHeight ? <span>{projectInfo.wallHeight} ceilings</span> : null}
                  {projectInfo.garageSqFt ? <span>+{projectInfo.garageSqFt} SF garage</span> : null}
                  {projectInfo.roofPitch ? <span>{projectInfo.roofPitch} pitch</span> : null}
                  {projectInfo.roofType ? <span className="capitalize">{projectInfo.roofType}</span> : null}
                  {pagesAnalyzed > 0 && <span className="text-green-600 font-medium">{pagesAnalyzed} plan page{pagesAnalyzed !== 1 ? 's' : ''} read</span>}
                </div>
              </div>
              <div className="flex-shrink-0 text-right flex flex-col items-end gap-1">
                <p className="text-xs text-blue-600 font-medium">Subtotal</p>
                <p className="text-lg font-bold text-blue-800">{fmt(grandTotal)}</p>
                {pdfObjectUrl && (
                  <button
                    onClick={() => setShowPdfPreview(true)}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                  >
                    <span className="material-symbols-outlined text-sm">picture_as_pdf</span>
                    View Plans
                  </button>
                )}
              </div>
            </div>

            {/* Edit note */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800 flex gap-2">
              <span className="material-symbols-outlined text-amber-500 text-base flex-shrink-0 mt-0.5">edit_note</span>
              Review all quantities, unit prices, and descriptions before creating the quote. Click any cell to edit it inline.
            </div>

            {/* Markup / Contingency */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-800 text-sm">Contingency & Markup</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Added as a separate line item when the quote is saved</p>
                </div>
                {effectiveMarkupPct > 0 && (
                  <div className="text-right">
                    <p className="text-xs text-gray-500">With markup</p>
                    <p className="text-base font-bold text-gray-900">{fmt(grandTotalWithMarkup)}</p>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {[0, 5, 10, 15, 20].map(pct => (
                  <button
                    key={pct}
                    onClick={() => { setMarkupPct(pct); setMarkupCustom(''); }}
                    className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                      markupPct === pct && !markupCustom
                        ? 'bg-primary border-primary text-white'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {pct === 0 ? 'None' : `${pct}%`}
                  </button>
                ))}
                <div className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-2.5 py-1 focus-within:border-primary transition-colors">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={markupCustom}
                    onChange={e => { setMarkupCustom(e.target.value); setMarkupPct(0); }}
                    placeholder="Custom"
                    className="w-16 text-sm outline-none text-gray-700 bg-transparent"
                  />
                  <span className="text-sm text-gray-400">%</span>
                </div>
                {effectiveMarkupPct > 0 && (
                  <span className="text-sm text-gray-500 ml-1">
                    = <span className="font-medium text-gray-800">{fmt(markupAmount)}</span>
                  </span>
                )}
              </div>
            </div>

            {/* Line item tables */}
            {sections.map((section, si) => (
              <div key={si} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <input
                    type="text"
                    value={section.name}
                    onChange={e => renameSection(si, e.target.value)}
                    className="font-semibold text-gray-800 bg-transparent outline-none focus:bg-white focus:border focus:border-gray-300 focus:rounded px-1 -ml-1 flex-1 text-sm"
                  />
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-sm font-semibold text-gray-700">{fmt(sectionTotal(section))}</span>
                    <button onClick={() => addItem(si)} className="text-xs flex items-center gap-1 text-primary hover:underline">
                      <span className="material-symbols-outlined text-sm">add</span>Add
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-500 bg-gray-50/50 border-b border-gray-100">
                        <th className="text-left px-3 py-2 font-medium w-[22%]">Item</th>
                        <th className="text-left px-3 py-2 font-medium w-[27%]">Description</th>
                        <th className="text-left px-3 py-2 font-medium w-[8%]">Type</th>
                        <th className="text-right px-3 py-2 font-medium w-[8%]">Qty</th>
                        <th className="text-left px-3 py-2 font-medium w-[6%]">Unit</th>
                        <th className="text-right px-3 py-2 font-medium w-[11%]">Unit Price</th>
                        <th className="text-right px-3 py-2 font-medium w-[11%]">Total</th>
                        <th className="w-8" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {section.items.map((item, ii) => (
                        <tr key={ii} className={`group hover:bg-gray-50 ${item.rate_type === 'labour' ? 'bg-amber-50/40' : ''}`}>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={item.item_name}
                              onChange={e => updateItem(si, ii, 'item_name', e.target.value)}
                              className="w-full text-gray-900 bg-transparent outline-none focus:bg-white focus:border focus:border-gray-200 focus:rounded px-1 -ml-1 text-sm font-medium"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={item.description}
                              onChange={e => updateItem(si, ii, 'description', e.target.value)}
                              className="w-full text-gray-500 bg-transparent outline-none focus:bg-white focus:border focus:border-gray-200 focus:rounded px-1 -ml-1 text-xs"
                            />
                          </td>
                          <td className="px-3 py-2">
                            {item.rate_type === 'labour' && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200">T&amp;M</span>
                            )}
                            {item.rate_type === 'material' && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-600 border border-blue-200">SUPPLY</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={e => updateItem(si, ii, 'quantity', parseFloat(e.target.value) || 0)}
                              className="w-20 text-right text-gray-800 bg-transparent outline-none focus:bg-white focus:border focus:border-gray-200 focus:rounded px-1 text-sm"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={item.unit}
                              onChange={e => updateItem(si, ii, 'unit', e.target.value)}
                              className="w-14 text-gray-600 bg-transparent outline-none focus:bg-white focus:border focus:border-gray-200 focus:rounded px-1 text-sm"
                            />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <input
                              type="number"
                              step="0.01"
                              value={item.unit_price}
                              onChange={e => updateItem(si, ii, 'unit_price', parseFloat(e.target.value) || 0)}
                              className="w-24 text-right text-gray-800 bg-transparent outline-none focus:bg-white focus:border focus:border-gray-200 focus:rounded px-1 text-sm"
                            />
                          </td>
                          <td className="px-3 py-2 text-right font-medium text-gray-800">
                            {fmt(item.quantity * item.unit_price)}
                          </td>
                          <td className="px-1 py-2">
                            <button
                              onClick={() => removeItem(si, ii)}
                              className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 text-gray-300 hover:text-red-400 transition-all"
                            >
                              <span className="material-symbols-outlined text-sm">close</span>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}

            {/* Fixed vs T&M breakdown */}
            {labourTotal > 0 && (
              <div className="bg-white rounded-xl border border-amber-200 shadow-sm px-5 py-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Pricing Breakdown</p>
                <div className="flex flex-col gap-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Fixed-price items</span>
                    <span className="font-medium text-gray-900">{fmt(fixedTotal)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="flex items-center gap-1.5 text-amber-700">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 border border-amber-200">T&amp;M</span>
                      Time &amp; materials (est. hours × $88/HR)
                    </span>
                    <span className="font-medium text-amber-700">{fmt(labourTotal)}</span>
                  </div>
                  <div className="flex justify-between items-center border-t border-gray-100 pt-2 mt-1">
                    <span className="font-semibold text-gray-800">Subtotal</span>
                    <span className="font-semibold text-gray-900">{fmt(grandTotal)}</span>
                  </div>
                </div>
                <p className="text-[11px] text-gray-400 mt-3">T&amp;M items are estimated hours only — actual hours billed on completion. Adjust quantities above if conditions differ from plans.</p>
              </div>
            )}

            {/* Client email */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <button
                onClick={() => setShowEmail(v => !v)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-primary">mail</span>
                  <div className="text-left">
                    <p className="font-semibold text-gray-800">Client Email</p>
                    <p className="text-xs text-gray-500">AI-drafted email explaining the estimate — review before sending</p>
                  </div>
                </div>
                <span className={`material-symbols-outlined text-gray-400 transition-transform ${showEmail ? 'rotate-180' : ''}`}>expand_more</span>
              </button>
              {showEmail && (
                <div className="px-5 pb-5 border-t border-gray-100 space-y-3">
                  <div className="mt-4">
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Subject</label>
                    <input
                      type="text"
                      value={email.subject}
                      onChange={e => setEmail(p => ({ ...p, subject: e.target.value }))}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Body</label>
                    <textarea
                      rows={14}
                      value={email.body}
                      onChange={e => setEmail(p => ({ ...p, body: e.target.value }))}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none resize-y font-mono leading-relaxed"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* AI Judgements */}
            {judgements.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <button
                  onClick={() => setShowJudgements(v => !v)}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="material-symbols-outlined text-amber-500">psychology</span>
                    <div className="text-left">
                      <p className="font-semibold text-gray-800">AI Assumptions & Judgements</p>
                      <p className="text-xs text-gray-500">{judgements.length} items — every assumption Claude made</p>
                    </div>
                  </div>
                  <span className={`material-symbols-outlined text-gray-400 transition-transform ${showJudgements ? 'rotate-180' : ''}`}>expand_more</span>
                </button>
                {showJudgements && (
                  <ul className="px-5 pb-5 pt-1 border-t border-gray-100 space-y-2">
                    {judgements.map((j, i) => (
                      <li key={i} className="flex gap-2 text-sm text-gray-700">
                        <span className="text-amber-400 flex-shrink-0 mt-0.5">›</span>
                        {j}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Grand total + actions */}
            <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div>
                <p className="text-sm text-gray-500">
                  {effectiveMarkupPct > 0 ? 'Subtotal' : 'Estimated Subtotal'}
                  <span className="ml-2 text-xs text-gray-400">· {sections.length} sections · {sections.reduce((a, s) => a + s.items.length, 0)} items</span>
                </p>
                {effectiveMarkupPct > 0 ? (
                  <div className="space-y-0.5">
                    <p className="text-lg text-gray-500 line-through">{fmt(grandTotal)}</p>
                    <p className="text-2xl font-bold text-gray-900">{fmt(grandTotalWithMarkup)}</p>
                    <p className="text-xs text-green-700 font-medium">Includes {effectiveMarkupPct}% contingency ({fmt(markupAmount)})</p>
                  </div>
                ) : (
                  <>
                    <p className="text-2xl font-bold text-gray-900">{fmt(grandTotal)}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Excludes GST · No markup applied</p>
                  </>
                )}
              </div>
              <div className="flex gap-3">
                <button onClick={() => setStep(1)} className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
                  Start Over
                </button>
                <button
                  onClick={createQuote}
                  disabled={saving || sections.length === 0}
                  className="flex items-center gap-2 bg-primary text-white px-6 py-2.5 rounded-xl font-semibold text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
                >
                  {saving
                    ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    : <span className="material-symbols-outlined text-base">save</span>}
                  {saving ? 'Creating…' : 'Create Quote'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 5: Done ─────────────────────────────────────────────────── */}
        {step === 5 && (
          <div className="flex flex-col items-center justify-center min-h-[400px] gap-6 text-center">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
              <span className="material-symbols-outlined text-green-500 text-4xl">check_circle</span>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 mb-1">Quote Created</p>
              <p className="text-gray-500">{createdQuoteNumber} — {title}</p>
              <p className="text-sm text-gray-400 mt-1">
                {fmt(effectiveMarkupPct > 0 ? grandTotalWithMarkup : grandTotal)}{effectiveMarkupPct > 0 ? ` (incl. ${effectiveMarkupPct}% markup)` : ''} · {sections.length} sections · {sections.reduce((a, s) => a + s.items.length, 0)} line items
              </p>
            </div>
            <div className="flex flex-wrap gap-3 justify-center">
              <button
                onClick={() => navigate(quotePath(clientId, jobId, createdQuoteId!))}
                className="flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-xl font-semibold text-sm hover:bg-primary/90"
              >
                <span className="material-symbols-outlined text-base">open_in_new</span>
                Open Quote
              </button>
              {email.body && !emailSent && (
                <button
                  onClick={sendEmail}
                  disabled={sendingEmail}
                  className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold text-sm hover:bg-blue-700 disabled:opacity-50"
                >
                  {sendingEmail
                    ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    : <span className="material-symbols-outlined text-base">send</span>}
                  {sendingEmail ? 'Sending…' : 'Send Email to Client'}
                </button>
              )}
              {emailSent && (
                <div className="flex items-center gap-2 px-5 py-3 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm font-medium">
                  <span className="material-symbols-outlined text-base">check_circle</span>
                  Email sent
                </div>
              )}
              <button
                onClick={resetWizard}
                className="px-5 py-3 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                New Quote
              </button>
            </div>
          </div>
        )}

      </div>
    </MainLayout>
  );
};
